import { EntityList } from "../../components/Settings/EntityList";
import type {
  Entity,
  LogoKind,
  NewEntity,
  UpdateEntity,
} from "../../api/types";

type EntityListPageConfig<
  Id extends string,
  New extends NewEntity<string>,
  Update extends UpdateEntity<Id, string>,
> = {
  title: string;
  entityType: LogoKind;
  useList: () => { data: Entity<Id, string>[] | undefined; isLoading: boolean };
  // useAdd must resolve to the created entity — EntityList uses its id to
  // upload the chosen logo after creation.
  useAdd: () => {
    mutateAsync: (entity: New) => Promise<Entity<Id, string>>;
  };
  useUpdate: () => { mutateAsync: (entity: Update) => Promise<unknown> };
};

/**
 * Builds a Settings page that renders an EntityList backed by the given
 * list/add/update query hooks. The entity pages (categories, groups, sources,
 * expense types, payees) differ only by title, entityType and hooks.
 */
export const createEntityListPage = <
  Id extends string,
  New extends NewEntity<string>,
  Update extends UpdateEntity<Id, string>,
>({
  title,
  entityType,
  useList,
  useAdd,
  useUpdate,
}: EntityListPageConfig<Id, New, Update>) => {
  const Page = () => {
    const { data, isLoading } = useList();
    const add = useAdd();
    const update = useUpdate();

    return (
      <EntityList<Id>
        title={title}
        entityType={entityType}
        data={data ?? []}
        onAdd={(entity) => add.mutateAsync(entity as New)}
        onUpdate={(entity) => update.mutateAsync(entity as Update)}
        isLoading={isLoading}
      />
    );
  };
  Page.displayName = `${title.replace(/\s+/g, "")}Page`;
  return Page;
};
